import { memo, useCallback } from 'react'

import { CorporateFare, Logout, Settings as SettingsIcon } from '@mui/icons-material'
import { Avatar, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { ListItemButtonLink } from 'components/LinkedMUIComponents'
import { useUserInfo } from 'hooks/useUserInfo'
import { organizationsOverviewRoute } from 'pages/NWPlatform/Organizations'
import { authService } from 'services/auth'
import { getAvatarColor, getContrastText, getInitials } from 'utils/avatar'

type UserProps = {
  onOpenSettings: () => void
}

const UserRaw = ({ onOpenSettings }: UserProps) => {
  const { t } = useTranslation()
  const handleClose = useCloseHandler()
  const { data: userInfo } = useUserInfo()

  const displayName = userInfo?.name || [userInfo?.givenName, userInfo?.familyName].filter(Boolean).join(' ') || null

  const handleSignOut = useCallback(async () => {
    try {
      await authService.logout()
    } catch {
      // Logout failed — redirect to login anyway
    }
    authService.stopAutoRefresh()
    authService.login()
  }, [])

  const handleOpenSettings = useCallback(() => {
    onOpenSettings()
    handleClose()
  }, [handleClose, onOpenSettings])

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Box sx={{ mt: 1, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 2 }}>
        <Avatar
          sx={{
            width: 160,
            height: 160,
            fontSize: 56,
            bgcolor: getAvatarColor(displayName),
            color: `${getContrastText(getAvatarColor(displayName))} !important`,
          }}
          alt={displayName || t('profile')}
          src={userInfo?.picture || undefined}
        >
          {!userInfo?.picture && getInitials(displayName, userInfo?.givenName, userInfo?.familyName)}
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {displayName && <Typography variant="heading-lg">{displayName}</Typography>}
          {userInfo?.email && <Typography variant="text">{userInfo.email}</Typography>}
        </Box>
      </Box>
      <List sx={{ display: 'flex', flexDirection: 'column', rowGap: 1 }} disablePadding>
        <ListItem disableGutters disablePadding>
          <ListItemButtonLink to={organizationsOverviewRoute.to} onClick={handleClose}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <CorporateFare />
            </ListItemIcon>
            <ListItemText primary="My Organizations" />
          </ListItemButtonLink>
        </ListItem>

        <ListItem disableGutters disablePadding>
          <ListItemButton onClick={handleOpenSettings}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Personal Settings" />
          </ListItemButton>
        </ListItem>

        <ListItem disableGutters disablePadding>
          <ListItemButton onClick={handleSignOut}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  )
}

export const User = memo(UserRaw)
